import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Tooltip, MenuItem, Select, FormControl,
  InputLabel, Pagination, CircularProgress, Alert, Dialog,
  DialogActions, TableSortLabel, LinearProgress, Collapse, Divider,
  Badge, Checkbox, Slide, Fade, ButtonGroup,
} from "@mui/material";
import SearchIcon          from "@mui/icons-material/Search";
import AddIcon             from "@mui/icons-material/Add";
import EditIcon            from "@mui/icons-material/Edit";
import DeleteIcon          from "@mui/icons-material/Delete";
import VisibilityIcon      from "@mui/icons-material/Visibility";
import SwapHorizIcon       from "@mui/icons-material/SwapHoriz";
import PictureAsPdfIcon    from "@mui/icons-material/PictureAsPdf";
import CalendarTodayIcon   from "@mui/icons-material/CalendarToday";
import CloseIcon           from "@mui/icons-material/Close";
import InventoryIcon       from "@mui/icons-material/Inventory2";
import TuneIcon            from "@mui/icons-material/Tune";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import ArchiveIcon         from "@mui/icons-material/Archive";
import SelectAllIcon       from "@mui/icons-material/SelectAll";
import DeselectIcon        from "@mui/icons-material/Deselect";
import FileUploadIcon      from "@mui/icons-material/FileUpload";
import FileDownloadIcon    from "@mui/icons-material/FileDownload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PlaylistAddIcon     from "@mui/icons-material/PlaylistAdd";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import TableRowsIcon       from "@mui/icons-material/TableRows";
import UnfoldMoreIcon      from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon      from "@mui/icons-material/UnfoldLess";
import LayersIcon          from "@mui/icons-material/Layers";
import api from "../../../services/api";
import BulkItemModal from "./BulkItemModal";

const C = {
  primary:    "#2563EB", primaryBg:  "#EFF6FF",
  surface:    "#FFFFFF", surfaceAlt: "#F8FAFC",
  border:     "#E2E8F0", borderHov:  "#CBD5E1",
  text1:      "#0F172A", text2:      "#475569", text3: "#94A3B8",
  success:    "#16A34A", successBg:  "#F0FDF4", successBdr: "#BBF7D0",
  warn:       "#D97706", warnBg:     "#FFFBEB", warnBdr:    "#FDE68A",
  danger:     "#DC2626", dangerBg:   "#FEF2F2", dangerBdr:  "#FECACA",
  info:       "#2563EB", infoBg:     "#EFF6FF", infoBdr:    "#BFDBFE",
  neutral:    "#64748B", neutralBg:  "#F1F5F9", neutralBdr: "#CBD5E1",
  amber:      "#B45309", amberBg:    "#FFFBEB", amberBdr:   "#FDE68A",
};

const CONDITION_MAP = {
  baik:         { label: "Baik",         color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  cukup_baik:   { label: "Cukup Baik",   color: C.info,    bg: C.infoBg,    border: C.infoBdr,    dot: "#3B82F6" },
  rusak_ringan: { label: "Rusak Ringan",  color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  rusak_berat:  { label: "Rusak Berat",   color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};
const STATUS_MAP = {
  aktif:           { label: "Aktif",           color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  tidak_aktif:     { label: "Tidak Aktif",     color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" },
  dipinjam:        { label: "Dipinjam",        color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  dalam_perbaikan: { label: "Dalam Perbaikan", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};

const thSx = {
  fontSize: "0.7rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`,
  py: "10px", px: "12px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};
const tdSx = {
  py: "10px", px: "12px",
  borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle",
};
const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

function StatusChip({ map, value, label }) {
  const cfg = map[value] || { label: value || "—", color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
  const displayLabel = label || cfg.label;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px", whiteSpace: "nowrap" }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {displayLabel}
      </Typography>
    </Box>
  );
}

function ActionBtn({ title, icon, hoverColor, hoverBg, onClick }) {
  return (
    <Tooltip title={title} arrow>
      <IconButton size="small" onClick={onClick} sx={{
        width: 28, height: 28, borderRadius: "6px", color: C.text3,
        transition: "all 0.15s",
        "&:hover": { color: hoverColor, backgroundColor: hoverBg },
      }}>
        {icon}
      </IconButton>
    </Tooltip>
  );
}

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

function BulkActionBar({ selectedIds, onClear, onBulkArchive, onBulkExportPdf, bulkArchiving, bulkExporting }) {
  const count = selectedIds.length;
  return (
    <Slide direction="up" in={count > 0} mountOnEnter unmountOnExit>
      <Box sx={{
        position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
        zIndex: 1300, display: "flex", alignItems: "center", gap: 1.5,
        px: 2.5, py: 1.5, backgroundColor: C.text1, borderRadius: "14px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
        minWidth: 420, border: `1px solid rgba(255,255,255,0.08)`,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 0.5, backgroundColor: C.primary, borderRadius: "8px", flexShrink: 0 }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff", lineHeight: 1 }}>
            {count} dipilih
          </Typography>
        </Box>
        <Box sx={{ flex: 1, height: 1, backgroundColor: "rgba(255,255,255,0.1)" }} />
        <Button size="small"
          startIcon={bulkExporting ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <PictureAsPdfIcon sx={{ fontSize: "14px !important" }} />}
          onClick={onBulkExportPdf} disabled={bulkExporting || bulkArchiving}
          sx={{ textTransform: "none", fontSize: "0.78rem", fontWeight: 500,
            color: "#E2E8F0", borderRadius: "8px", height: 32, px: 1.5,
            border: "1px solid rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.08)", color: "#fff" },
            "&:disabled": { color: "rgba(255,255,255,0.3)" } }}>
          {bulkExporting ? "Mengekspor..." : "Export PDF"}
        </Button>
        <Button size="small"
          startIcon={bulkArchiving ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <ArchiveIcon sx={{ fontSize: "14px !important" }} />}
          onClick={onBulkArchive} disabled={bulkArchiving || bulkExporting}
          sx={{ textTransform: "none", fontSize: "0.78rem", fontWeight: 600,
            color: "#fff", borderRadius: "8px", height: 32, px: 1.5,
            backgroundColor: C.danger, "&:hover": { backgroundColor: "#B91C1C" },
            "&:disabled": { backgroundColor: "rgba(220,38,38,0.4)", color: "rgba(255,255,255,0.5)" } }}>
          {bulkArchiving ? "Mengarsipkan..." : "Arsipkan Semua"}
        </Button>
        <Tooltip title="Batal pilih" arrow>
          <IconButton size="small" onClick={onClear} sx={{
            color: "rgba(255,255,255,0.5)", width: 28, height: 28, borderRadius: "6px",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Slide>
  );
}

export default function ItemsIndex() {
  const navigate = useNavigate();

  const [viewMode,        setViewMode]        = useState("grouped"); // "grouped" | "flat"
  const [groupedItems,    setGroupedItems]    = useState([]);
  const [expandedGroups,  setExpandedGroups]  = useState({});
  const [totalGroups,     setTotalGroups]     = useState(0);

  const [items,           setItems]           = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");
  const [page,            setPage]            = useState(1);
  const [totalPages,      setTotalPages]      = useState(1);
  const [totalItems,      setTotalItems]      = useState(0);
  const [searchInput,     setSearchInput]     = useState("");
  const [search,          setSearch]          = useState("");
  const [filterStatus,    setFilterStatus]    = useState("");
  const [filterCondition, setFilterCondition] = useState("");
  const [sortOrder,       setSortOrder]       = useState("desc");
  const [dateFrom,        setDateFrom]        = useState("");
  const [dateTo,          setDateTo]          = useState("");
  const [deleteId,        setDeleteId]        = useState(null);
  const [deleteItem,      setDeleteItem]      = useState(null);
  const [deleting,        setDeleting]        = useState(false);
  const [exportingPdf,    setExportingPdf]    = useState(false);
  const [showFilters,     setShowFilters]     = useState(false);
  const [archivedCount,   setArchivedCount]   = useState(0);
  const [selectedIds,     setSelectedIds]     = useState([]);
  const [bulkArchiving,   setBulkArchiving]   = useState(false);
  const [bulkExporting,   setBulkExporting]   = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // ── Import state ──
  const [importOpen,   setImportOpen]   = useState(false);
  const [importFile,   setImportFile]   = useState(null);
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const debounceRef  = useRef(null);

  // ── Bulk Input State ──
  const location = useLocation();
  const [rooms,           setRooms]           = useState([]);
  const [bulkOpen,        setBulkOpen]        = useState(false);
  const [bulkSuccessMsg,  setBulkSuccessMsg]  = useState("");

  useEffect(() => {
    if (location.state?.message) {
      setBulkSuccessMsg(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await api.get("/rooms", { params: { simple: true } });
      const data = Array.isArray(res.data) ? res.data
        : Array.isArray(res.data?.data?.data) ? res.data.data.data
        : res.data?.data ?? [];
      setRooms(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const currentPageIds = useMemo(() => {
    return viewMode === "grouped"
      ? groupedItems.flatMap(g => (g.items || []).map(i => i.id))
      : items.map(i => i.id);
  }, [viewMode, groupedItems, items]);

  const allPageSelected = useMemo(() => {
    return currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.includes(id));
  }, [currentPageIds, selectedIds]);

  const somePageSelected = useMemo(() => {
    return currentPageIds.some(id => selectedIds.includes(id)) && !allPageSelected;
  }, [currentPageIds, selectedIds, allPageSelected]);

  const handleOpenBulk = useCallback(() => setBulkOpen(true), []);
  const handleCloseBulk = useCallback(() => setBulkOpen(false), []);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const toggleExpandAll = () => {
    const allExpanded = groupedItems.length > 0 && groupedItems.every(g => expandedGroups[g.name]);
    const nextState = {};
    groupedItems.forEach(g => { nextState[g.name] = !allExpanded; });
    setExpandedGroups(nextState);
  };

  const handleSelectGroup = (group) => {
    const groupItemIds = (group.items || []).map(i => i.id);
    const allGroupSelected = groupItemIds.length > 0 && groupItemIds.every(id => selectedIds.includes(id));
    if (allGroupSelected) {
      setSelectedIds(prev => prev.filter(id => !groupItemIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...groupItemIds])]);
    }
  };

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  useEffect(() => { setSelectedIds([]); }, [page, search, filterStatus, filterCondition, sortOrder, dateFrom, dateTo, viewMode]);

  const handleSelectAll = () => {
    if (allPageSelected) setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
    else setSelectedIds(prev => [...new Set([...prev, ...currentPageIds])]);
  };
  const handleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const fetchArchivedCount = useCallback(async () => {
    try {
      const res = await api.get("/items/archived", { params: { count_only: true, per_page: 1 } });
      setArchivedCount(res.data.data.total ?? 0);
    } catch { /* silent */ }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page, per_page: 10, sort: sortOrder };
      if (search)          params.search    = search;
      if (filterStatus)    params.status    = filterStatus;
      if (filterCondition) params.condition = filterCondition;
      if (dateFrom)        params.date_from = dateFrom;
      if (dateTo)          params.date_to   = dateTo;

      if (viewMode === "grouped") {
        const res = await api.get("/items/grouped", { params });
        const groups = res.data.data.data ?? [];
        setGroupedItems(groups);
        setTotalPages(res.data.data.last_page ?? 1);
        setTotalGroups(res.data.data.total ?? 0);
        setTotalItems(res.data.data.total_items ?? 0);

        if (search) {
          const autoExpand = {};
          groups.forEach(g => { autoExpand[g.name] = true; });
          setExpandedGroups(autoExpand);
        }
      } else {
        const res = await api.get("/items", { params });
        setItems(res.data.data.data          ?? []);
        setTotalPages(res.data.data.last_page ?? 1);
        setTotalItems(res.data.data.total     ?? 0);
      }
    } catch {
      setError("Gagal memuat data barang.");
    } finally { setLoading(false); }
  }, [page, search, filterStatus, filterCondition, sortOrder, dateFrom, dateTo, viewMode]);
 
  const handleSuccessBulk = useCallback((msg) => {
    setBulkOpen(false);
    setBulkSuccessMsg(msg);
    fetchItems();
  }, [fetchItems]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchArchivedCount(); }, [fetchArchivedCount]);
  useEffect(() => { setPage(1); }, [filterStatus, filterCondition, sortOrder, dateFrom, dateTo, viewMode]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/items/${deleteId}`);
      setDeleteId(null); setDeleteItem(null);
      fetchItems(); fetchArchivedCount();
    } catch { setError("Gagal mengarsipkan barang."); }
    finally  { setDeleting(false); }
  };

  const handleBulkArchive = async () => {
    setBulkArchiving(true); setBulkConfirmOpen(false);
    try {
      await api.post("/items/bulk-archive", { ids: selectedIds });
      setSelectedIds([]); fetchItems(); fetchArchivedCount();
    } catch (err) { setError(err.response?.data?.message || "Gagal mengarsipkan barang yang dipilih."); }
    finally { setBulkArchiving(false); }
  };

  const handleBulkExportPdf = async () => {
    setBulkExporting(true);
    try {
      const res = await api.post("/items/bulk-export-pdf", { ids: selectedIds }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `inventaris-pilihan-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { setError("Gagal mengekspor PDF item yang dipilih."); }
    finally  { setBulkExporting(false); }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = {};
      if (search)          params.search    = search;
      if (filterStatus)    params.status    = filterStatus;
      if (filterCondition) params.condition = filterCondition;
      if (dateFrom)        params.date_from = dateFrom;
      if (dateTo)          params.date_to   = dateTo;
      const res = await api.get("/items/export-pdf", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `inventaris-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { setError("Gagal mengekspor PDF."); }
    finally  { setExportingPdf(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get("/items/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = "template-import-barang.xlsx";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { setError("Gagal mengunduh template."); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await api.post("/items/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      setImportFile(null);
      fetchItems(); fetchArchivedCount();
    } catch (err) {
      setError(err.response?.data?.message || "Gagal mengimpor file.");
    } finally { setImporting(false); }
  };

  const closeImport = () => {
    if (importing) return;
    setImportOpen(false); setImportResult(null); setImportFile(null);
  };

  const handleClearDates = () => { setDateFrom(""); setDateTo(""); };
  const toggleSort = () => setSortOrder(p => p === "desc" ? "asc" : "desc");

  const activeFilterCount = [filterStatus, filterCondition, dateFrom || dateTo].filter(Boolean).length;
  const hasDateFilter     = dateFrom || dateTo;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.primaryBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <InventoryIcon sx={{ fontSize: 20, color: C.primary }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: C.text1, lineHeight: 1.2 }}>
                Daftar Barang
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
                Kelola inventaris dan lacak perpindahan barang
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            {/* Arsip */}
            <Tooltip title={archivedCount > 0 ? `${archivedCount} barang diarsipkan` : "Lihat arsip barang"} arrow>
              <Badge badgeContent={archivedCount || null} color="warning"
                sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", minWidth: 16, height: 16 } }}>
                <Button variant="outlined" size="small"
                  startIcon={<ArchiveIcon sx={{ fontSize: "15px !important" }} />}
                  onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/archived`)}
                  sx={{ borderColor: C.amberBdr, color: C.amber, textTransform: "none",
                    borderRadius: "8px", fontSize: "0.8rem", fontWeight: 500, height: 34,
                    "&:hover": { borderColor: C.amber, backgroundColor: C.amberBg } }}>
                  Arsip
                </Button>
              </Badge>
            </Tooltip>

            {/* Export PDF */}
            <Button variant="outlined" size="small"
              startIcon={exportingPdf ? <CircularProgress size={13} sx={{ color: C.danger }} /> : <PictureAsPdfIcon sx={{ fontSize: "15px !important" }} />}
              onClick={handleExportPdf} disabled={exportingPdf}
              sx={{ borderColor: C.dangerBdr, color: C.danger, textTransform: "none",
                borderRadius: "8px", fontSize: "0.8rem", fontWeight: 500, height: 34,
                "&:hover": { borderColor: C.danger, backgroundColor: C.dangerBg } }}>
              {exportingPdf ? "Mengekspor..." : "Export PDF"}
            </Button>

            {/* Import Excel */}
            <Button variant="outlined" size="small"
              startIcon={<FileUploadIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}
              sx={{ borderColor: C.border, color: C.text2, textTransform: "none",
                borderRadius: "8px", fontSize: "0.8rem", fontWeight: 500, height: 34,
                "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg } }}>
              Import Excel
            </Button>

            {/* Input Barang Sekaligus */}
            <Button variant="outlined" size="small"
              startIcon={<PlaylistAddIcon sx={{ fontSize: "17px !important" }} />}
              onClick={handleOpenBulk}
              sx={{ borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg, textTransform: "none",
                borderRadius: "8px", fontSize: "0.8rem", fontWeight: 600, height: 34, px: 1.5,
                "&:hover": { backgroundColor: "#DBEAFE", borderColor: "#1D4ED8" } }}>
              Input Sekaligus
            </Button>

            {/* Tambah Barang (Manual) */}
            <Button variant="contained" size="small"
              startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/create`)}
              sx={{ backgroundColor: C.primary, color: "#fff", textTransform: "none",
                borderRadius: "8px", fontSize: "0.8rem", fontWeight: 600, height: 34, px: 2,
                boxShadow: "0 4px 12px rgba(37,99,235,0.2)",
                "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 6px 16px rgba(37,99,235,0.3)" } }}>
              Tambah Barang
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Alert ── */}
      {bulkSuccessMsg && (
        <Alert severity="success" onClose={() => setBulkSuccessMsg("")}
          sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.successBdr}`, backgroundColor: C.successBg }}>
          {bulkSuccessMsg}
        </Alert>
      )}

      {/* ── Alert ── */}
      {error && (
        <Alert severity="error" onClose={() => setError("")}
          sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.dangerBdr}` }}>
          {error}
        </Alert>
      )}

      {/* ── Filter Card ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px",
        backgroundColor: C.surface, mb: 2, overflow: "hidden" }}>
        <Box sx={{ p: "12px 16px", display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <TextField size="small" placeholder="Cari nama atau serial number..."
            value={searchInput} onChange={handleSearchInput}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: C.text3 }} /></InputAdornment>,
              endAdornment: loading && searchInput ? <InputAdornment position="end"><CircularProgress size={13} sx={{ color: C.primary }} /></InputAdornment> : null,
            }}
            sx={{ ...inputBase, flex: "1 1 220px", maxWidth: 320 }} />

          <Badge badgeContent={activeFilterCount} color="primary"
            sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", minWidth: 16, height: 16 } }}>
            <Button size="small" variant={showFilters ? "contained" : "outlined"}
              startIcon={<TuneIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setShowFilters(p => !p)}
              sx={{ height: 34, borderRadius: "8px", textTransform: "none", fontSize: "0.8rem", fontWeight: 500,
                ...(showFilters
                  ? { backgroundColor: C.primary, "&:hover": { backgroundColor: "#1D4ED8" } }
                  : { borderColor: C.border, color: C.text2, "&:hover": { borderColor: C.primary, color: C.primary } }) }}>
              Filter
            </Button>
          </Badge>

          {activeFilterCount > 0 && (
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
              {filterStatus && (
                <Chip label={STATUS_MAP[filterStatus]?.label || filterStatus} size="small"
                  onDelete={() => setFilterStatus("")}
                  deleteIcon={<CloseIcon sx={{ fontSize: "11px !important" }} />}
                  sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600,
                    backgroundColor: C.primaryBg, color: C.primary, border: `1px solid ${C.infoBdr}` }} />
              )}
              {filterCondition && (
                <Chip label={CONDITION_MAP[filterCondition]?.label || filterCondition} size="small"
                  onDelete={() => setFilterCondition("")}
                  deleteIcon={<CloseIcon sx={{ fontSize: "11px !important" }} />}
                  sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600,
                    backgroundColor: C.primaryBg, color: C.primary, border: `1px solid ${C.infoBdr}` }} />
              )}
              {hasDateFilter && (
                <Chip
                  label={dateFrom && dateTo ? `${dateFrom} – ${dateTo}` : dateFrom ? `Dari ${dateFrom}` : `Sampai ${dateTo}`}
                  size="small" onDelete={handleClearDates}
                  deleteIcon={<CloseIcon sx={{ fontSize: "11px !important" }} />}
                  icon={<CalendarTodayIcon sx={{ fontSize: "11px !important", color: `${C.primary} !important` }} />}
                  sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600,
                    backgroundColor: C.primaryBg, color: C.primary, border: `1px solid ${C.infoBdr}` }} />
              )}
              <Button size="small"
                onClick={() => { setFilterStatus(""); setFilterCondition(""); handleClearDates(); }}
                sx={{ fontSize: "0.72rem", color: C.text3, textTransform: "none",
                  p: "0 4px", minWidth: 0, "&:hover": { color: C.danger } }}>
                Hapus semua
              </Button>
            </Box>
          )}
        </Box>

        <Collapse in={showFilters}>
          <Divider sx={{ borderColor: C.border }} />
          <Box sx={{ p: "12px 16px", display: "flex", gap: 1.5, flexWrap: "wrap",
            alignItems: "center", backgroundColor: C.surfaceAlt }}>
            <FormControl size="small" sx={{ minWidth: 140, ...inputBase }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value=""><em>Semua Status</em></MenuItem>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: v.dot }} />{v.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, ...inputBase }}>
              <InputLabel>Kondisi</InputLabel>
              <Select value={filterCondition} label="Kondisi" onChange={(e) => setFilterCondition(e.target.value)}>
                <MenuItem value=""><em>Semua Kondisi</em></MenuItem>
                {Object.entries(CONDITION_MAP).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: v.dot }} />{v.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ width: 1, height: 28, backgroundColor: C.border }} />
            <TextField size="small" type="date" label="Dari tanggal" value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); if (dateTo && e.target.value > dateTo) setDateTo(""); }}
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 13, color: dateFrom ? C.primary : C.text3 }} /></InputAdornment> }}
              sx={{ ...inputBase, width: 170 }} />
            <TextField size="small" type="date" label="Sampai tanggal" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateFrom || undefined }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 13, color: dateTo ? C.primary : C.text3 }} /></InputAdornment> }}
              sx={{ ...inputBase, width: 170 }} />
            {hasDateFilter && (
              <Tooltip title="Reset tanggal">
                <IconButton size="small" onClick={handleClearDates}
                  sx={{ border: `1px solid ${C.dangerBdr}`, color: C.danger,
                    borderRadius: "8px", width: 32, height: 32, "&:hover": { backgroundColor: C.dangerBg } }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* ── Table Card ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ px: 2, py: "10px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: `1px solid ${C.border}`,
          backgroundColor: selectedIds.length > 0 ? C.primaryBg : C.surfaceAlt, transition: "background 0.2s", flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text3, fontWeight: 500 }}>
              {loading ? "Memuat data..." : viewMode === "grouped"
                ? `${totalGroups > 0 ? `${(page - 1) * 10 + 1}–${Math.min(page * 10, totalGroups)} dari ${totalGroups} jenis barang` : "0 jenis barang"} (${totalItems} total unit)`
                : `${totalItems > 0 ? `${(page - 1) * 10 + 1}–${Math.min(page * 10, totalItems)} dari ${totalItems}` : "0"} barang`}
            </Typography>
            {selectedIds.length > 0 && (
              <Fade in>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: C.primary }} />
                  <Typography sx={{ fontSize: "0.75rem", color: C.primary, fontWeight: 600 }}>
                    {selectedIds.length} item dipilih
                  </Typography>
                  <Button size="small" onClick={() => setSelectedIds([])}
                    sx={{ fontSize: "0.7rem", color: C.text3, textTransform: "none",
                      p: "0 4px", minWidth: 0, "&:hover": { color: C.danger } }}>
                    Batalkan
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loading && (
              <LinearProgress sx={{ width: 80, height: 3, borderRadius: 4,
                backgroundColor: C.border, "& .MuiLinearProgress-bar": { backgroundColor: C.primary } }} />
            )}

            {/* View Mode Toggle */}
            <ButtonGroup size="small" sx={{ borderRadius: "8px", overflow: "hidden" }}>
              <Tooltip title="Kelompokkan barang yang sama (klik nama barang untuk lihat serial number)" arrow>
                <Button
                  variant={viewMode === "grouped" ? "contained" : "outlined"}
                  onClick={() => { setViewMode("grouped"); setPage(1); }}
                  startIcon={<LayersIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    textTransform: "none", fontSize: "0.74rem", fontWeight: 600, height: 30, px: 1.2,
                    backgroundColor: viewMode === "grouped" ? C.primary : "transparent",
                    color: viewMode === "grouped" ? "#fff" : C.text2,
                    borderColor: C.border,
                    "&:hover": { backgroundColor: viewMode === "grouped" ? "#1D4ED8" : C.surfaceAlt },
                  }}>
                  Dikelompokkan
                </Button>
              </Tooltip>
              <Tooltip title="Tampilkan semua baris serial number" arrow>
                <Button
                  variant={viewMode === "flat" ? "contained" : "outlined"}
                  onClick={() => { setViewMode("flat"); setPage(1); }}
                  startIcon={<TableRowsIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    textTransform: "none", fontSize: "0.74rem", fontWeight: 600, height: 30, px: 1.2,
                    backgroundColor: viewMode === "flat" ? C.primary : "transparent",
                    color: viewMode === "flat" ? "#fff" : C.text2,
                    borderColor: C.border,
                    "&:hover": { backgroundColor: viewMode === "flat" ? "#1D4ED8" : C.surfaceAlt },
                  }}>
                  Semua Baris
                </Button>
              </Tooltip>
            </ButtonGroup>

            {/* Buka Semua / Tutup Semua (Grouped Mode Only) */}
            {viewMode === "grouped" && groupedItems.length > 0 && (
              <Tooltip title="Buka atau tutup semua daftar serial number di halaman ini" arrow>
                <Button size="small" onClick={toggleExpandAll}
                  startIcon={groupedItems.every(g => expandedGroups[g.name])
                    ? <UnfoldLessIcon sx={{ fontSize: "14px !important" }} />
                    : <UnfoldMoreIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    textTransform: "none", fontSize: "0.72rem", color: C.text2, fontWeight: 600,
                    height: 30, px: 1.2, border: `1px solid ${C.border}`, borderRadius: "8px",
                    backgroundColor: C.surface,
                    "&:hover": { backgroundColor: C.surfaceAlt, borderColor: C.borderHov },
                  }}>
                  {groupedItems.every(g => expandedGroups[g.name]) ? "Tutup Semua" : "Buka Semua"}
                </Button>
              </Tooltip>
            )}

            {!loading && currentPageIds.length > 0 && (
              <Tooltip title={allPageSelected ? "Batal pilih halaman ini" : "Pilih semua di halaman ini"} arrow>
                <IconButton size="small" onClick={handleSelectAll}
                  sx={{ width: 30, height: 30, borderRadius: "8px",
                    color: allPageSelected ? C.primary : C.text3,
                    backgroundColor: allPageSelected ? C.primaryBg : "transparent",
                    border: `1px solid ${allPageSelected ? C.primary : C.border}`,
                    "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg } }}>
                  {allPageSelected ? <DeselectIcon sx={{ fontSize: 14 }} /> : <SelectAllIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                <TableCell sx={{ ...thSx, width: 44, px: "8px" }}>
                  <Checkbox size="small" checked={allPageSelected} indeterminate={somePageSelected}
                    onChange={handleSelectAll} disabled={loading || currentPageIds.length === 0}
                    sx={{ p: 0.5, color: C.text3, "&.Mui-checked": { color: C.primary },
                      "&.MuiCheckbox-indeterminate": { color: C.primary } }} />
                </TableCell>
                <TableCell sx={{ ...thSx, width: 60 }}>
                  <TableSortLabel active direction={sortOrder} onClick={toggleSort}
                    sx={{ fontSize: "0.7rem", fontWeight: 700, color: `${C.text3} !important`,
                      "& .MuiTableSortLabel-icon": { fontSize: 13, color: `${C.text3} !important` } }}>
                    {sortOrder === "desc" ? "Terbaru" : "Terlama"}
                  </TableSortLabel>
                </TableCell>

                {viewMode === "grouped" ? (
                  <>
                    <TableCell sx={thSx}>Nama Barang</TableCell>
                    <TableCell sx={{ ...thSx, width: 110 }}>Total Unit</TableCell>
                    <TableCell sx={thSx}>Rentang Serial Number</TableCell>
                    <TableCell sx={thSx}>Kondisi</TableCell>
                    <TableCell sx={thSx}>Status</TableCell>
                    <TableCell sx={thSx}>Ruangan</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right", pr: 2 }}>Serial Number</TableCell>
                  </>
                ) : (
                  ["Serial Number", "Nama Barang", "Qty", "Kondisi", "Status", "Tgl. Masuk", "Ruangan", "Aksi"].map(h => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, border: 0 }}>
                    <CircularProgress size={28} thickness={4} sx={{ color: C.primary }} />
                    <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: C.text3 }}>Memuat data...</Typography>
                  </TableCell>
                </TableRow>
              ) : (viewMode === "grouped" ? groupedItems.length === 0 : items.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, border: 0 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: "12px", backgroundColor: C.surfaceAlt,
                      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <InventoryIcon sx={{ fontSize: 24, color: C.text3 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.875rem", color: C.text2, fontWeight: 500 }}>
                      {search ? `Tidak ditemukan hasil untuk "${search}"` : "Belum ada data barang"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.5 }}>
                      {search ? "Coba kata kunci lain atau hapus filter" : 'Klik "Tambah Barang" untuk mulai'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : viewMode === "grouped" ? (
                /* ── Mode Dikelompokkan (Accordion per Nama Barang) ── */
                groupedItems.map((group, idx) => {
                  const isExpanded = Boolean(expandedGroups[group.name]);
                  const groupItemIds = (group.items || []).map(i => i.id);
                  const allGroupSelected = groupItemIds.length > 0 && groupItemIds.every(id => selectedIds.includes(id));
                  const someGroupSelected = groupItemIds.some(id => selectedIds.includes(id)) && !allGroupSelected;

                  return (
                    <Box component="tbody" key={group.name} sx={{ display: "contents" }}>
                      {/* Baris Utama Kelompok Barang */}
                      <TableRow sx={{
                        backgroundColor: isExpanded ? "#F8FAFC" : allGroupSelected ? "#F0F7FF" : "transparent",
                        "&:hover": { backgroundColor: isExpanded ? "#F1F5F9" : "#F8FAFC" },
                        borderBottom: `1px solid ${C.border}`,
                        transition: "background 0.12s",
                      }}>
                        <TableCell sx={{ ...tdSx, px: "8px", width: 44 }}>
                          <Checkbox size="small" checked={allGroupSelected} indeterminate={someGroupSelected}
                            onChange={() => handleSelectGroup(group)}
                            sx={{ p: 0.5, color: C.text3, "&.Mui-checked": { color: C.primary } }} />
                        </TableCell>

                        <TableCell sx={{ ...tdSx, width: 60 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton size="small" onClick={() => toggleGroup(group.name)}
                              sx={{
                                width: 26, height: 26, borderRadius: "6px",
                                color: isExpanded ? C.primary : C.text3,
                                backgroundColor: isExpanded ? C.primaryBg : "transparent",
                                "&:hover": { backgroundColor: C.primaryBg, color: C.primary }
                              }}>
                              {isExpanded ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                            <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                              {(page - 1) * 10 + idx + 1}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Nama Barang */}
                        <TableCell sx={tdSx}>
                          <Box onClick={() => toggleGroup(group.name)} sx={{ cursor: "pointer", py: 0.25 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{
                                fontSize: "0.875rem", fontWeight: 700,
                                color: isExpanded ? C.primary : C.text1,
                                "&:hover": { color: C.primary },
                              }}>
                                {group.name}
                              </Typography>
                              {group.category && (
                                <Chip size="small" label={group.category}
                                  sx={{ height: 20, fontSize: "0.68rem", backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }} />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: "0.72rem", color: isExpanded ? C.primary : C.text3, mt: 0.25 }}>
                              {isExpanded ? "▲ Sembunyikan serial number" : `▼ Klik untuk melihat ${group.total_quantity} serial number`}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Total Unit */}
                        <TableCell sx={tdSx}>
                          <Box sx={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            px: 1.2, py: 0.5, borderRadius: "8px",
                            backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`
                          }}>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: C.primary, fontVariantNumeric: "tabular-nums" }}>
                              {group.total_quantity} {group.unit || "Unit"}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Rentang Serial Number */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Chip size="small" label={group.first_serial || "—"}
                              sx={{
                                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                                fontSize: "0.72rem", fontWeight: 600,
                                backgroundColor: "#fff", border: `1px solid ${C.border}`,
                              }} />
                            {group.first_serial && group.last_serial && group.first_serial !== group.last_serial && (
                              <>
                                <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>s/d</Typography>
                                <Chip size="small" label={group.last_serial}
                                  sx={{
                                    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                                    fontSize: "0.72rem", fontWeight: 600,
                                    backgroundColor: "#fff", border: `1px solid ${C.border}`,
                                  }} />
                              </>
                            )}
                          </Box>
                        </TableCell>

                        {/* Kondisi Summary */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {Object.entries(group.conditions || {}).map(([cond, count]) => (
                              <StatusChip key={cond} map={CONDITION_MAP} value={cond}
                                label={`${count} ${CONDITION_MAP[cond]?.label || cond}`} />
                            ))}
                          </Box>
                        </TableCell>

                        {/* Status Summary */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {Object.entries(group.statuses || {}).map(([st, count]) => (
                              <StatusChip key={st} map={STATUS_MAP} value={st}
                                label={`${count} ${STATUS_MAP[st]?.label || st}`} />
                            ))}
                          </Box>
                        </TableCell>

                        {/* Ruangan */}
                        <TableCell sx={tdSx}>
                          {(group.rooms || []).length > 0 ? (
                            <Typography sx={{ fontSize: "0.8rem", color: C.text1, fontWeight: 500 }}>
                              {group.rooms[0].name}
                              {group.rooms.length > 1 && (
                                <Typography component="span" sx={{ fontSize: "0.72rem", color: C.text3, ml: 0.5 }}>
                                  (+{group.rooms.length - 1} ruangan lain)
                                </Typography>
                              )}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: C.text3, fontSize: "0.78rem" }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Tombol Buka / Tutup */}
                        <TableCell sx={{ ...tdSx, textAlign: "right", pr: 2 }}>
                          <Button size="small" variant={isExpanded ? "contained" : "outlined"}
                            onClick={() => toggleGroup(group.name)}
                            endIcon={isExpanded
                              ? <KeyboardArrowDownIcon sx={{ fontSize: "14px !important" }} />
                              : <KeyboardArrowRightIcon sx={{ fontSize: "14px !important" }} />}
                            sx={{
                              textTransform: "none", fontSize: "0.72rem", fontWeight: 600,
                              borderRadius: "8px", py: 0.4, px: 1.2,
                              backgroundColor: isExpanded ? C.primary : "transparent",
                              color: isExpanded ? "#fff" : C.primary,
                              borderColor: isExpanded ? C.primary : C.infoBdr,
                              "&:hover": {
                                backgroundColor: isExpanded ? "#1D4ED8" : C.primaryBg,
                                borderColor: C.primary,
                              }
                            }}>
                            {isExpanded ? "Tutup" : `Lihat ${group.total_quantity} Serial`}
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Sub-Table Accordion yang Terbuka saat Nama Barang Diklik */}
                      <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                        <TableCell colSpan={9} sx={{ p: 0, borderBottom: isExpanded ? `1px solid ${C.border}` : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, pl: { xs: 2, md: 5 }, pr: 2, backgroundColor: "#F8FAFC", borderTop: `1px solid ${C.border}` }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: C.text1 }}>
                                    📋 Daftar Serial Number: {group.name}
                                  </Typography>
                                  <Chip size="small" label={`${group.total_quantity} ${group.unit || "Unit"}`}
                                    sx={{ fontSize: "0.7rem", height: 22, backgroundColor: C.primaryBg, color: C.primary, fontWeight: 700 }} />
                                </Box>
                                <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>
                                  Centang checkbox untuk ekspor PDF pilihan atau arsipkan massal
                                </Typography>
                              </Box>

                              <TableContainer component={Paper} elevation={0}
                                sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", backgroundColor: "#fff", maxHeight: 380, overflowY: "auto" }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                                      <TableCell sx={{ ...thSx, width: 40, py: 1, px: "8px" }}>
                                        <Checkbox size="small" checked={allGroupSelected} indeterminate={someGroupSelected}
                                          onChange={() => handleSelectGroup(group)}
                                          sx={{ p: 0.25, color: C.text3, "&.Mui-checked": { color: C.primary } }} />
                                      </TableCell>
                                      <TableCell sx={{ ...thSx, width: 45, py: 1 }}>#</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Serial Number</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Kondisi</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Status</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Ruangan</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Tgl. Masuk</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Aksi</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(group.items || []).map((subItem, sIdx) => {
                                      const isSubSelected = selectedIds.includes(subItem.id);
                                      return (
                                        <TableRow key={subItem.id} sx={{
                                          backgroundColor: isSubSelected ? "#EFF6FF" : "transparent",
                                          "&:hover": { backgroundColor: isSubSelected ? "#E0F2FE" : "#F8FAFC" },
                                          "&:hover .sub-actions": { opacity: 1 },
                                          borderBottom: `1px solid ${C.border}`,
                                        }}>
                                          <TableCell sx={{ ...tdSx, px: "8px", width: 40, py: 0.75 }}>
                                            <Checkbox size="small" checked={isSubSelected}
                                              onChange={() => handleSelectOne(subItem.id)}
                                              sx={{ p: 0.25, color: C.text3, "&.Mui-checked": { color: C.primary } }} />
                                          </TableCell>
                                          <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                            <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600 }}>
                                              {sIdx + 1}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                            <Typography sx={{
                                              fontSize: "0.78rem", fontWeight: 600, color: C.text1,
                                              fontFamily: "'JetBrains Mono', 'Courier New', monospace"
                                            }}>
                                              {subItem.serial_number || "—"}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                            <StatusChip map={CONDITION_MAP} value={subItem.condition} />
                                          </TableCell>
                                          <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                            <StatusChip map={STATUS_MAP} value={subItem.status} />
                                          </TableCell>
                                          <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                            {subItem.room ? (
                                              <Typography sx={{ fontSize: "0.78rem", color: C.text1 }}>
                                                {subItem.room.name} ({subItem.room.code})
                                              </Typography>
                                            ) : (
                                              <Typography sx={{ fontSize: "0.78rem", color: C.text3 }}>—</Typography>
                                            )}
                                          </TableCell>
                                          <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                            <Typography sx={{ fontSize: "0.75rem", color: C.text2 }}>
                                              {fmtDate(subItem.created_at)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                            <Box className="sub-actions" sx={{ display: "flex", gap: 0.5, opacity: { xs: 1, md: 0.6 }, transition: "opacity 0.15s" }}>
                                              <ActionBtn title="Lihat Detail" icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                hoverColor={C.primary} hoverBg={C.primaryBg}
                                                onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${subItem.id}`)} />
                                              <ActionBtn title="Edit" icon={<EditIcon sx={{ fontSize: 14 }} />}
                                                hoverColor={C.warn} hoverBg={C.warnBg}
                                                onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${subItem.id}/edit`)} />
                                              <ActionBtn title="Pindah Ruangan" icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                                                hoverColor={C.success} hoverBg={C.successBg}
                                                onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${subItem.id}/move`)} />
                                              <ActionBtn title="Arsipkan" icon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                                hoverColor={C.danger} hoverBg={C.dangerBg}
                                                onClick={() => { setDeleteId(subItem.id); setDeleteItem(subItem); }} />
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Box>
                  );
                })
              ) : (
                /* ── Mode Semua Baris (Flat View) ── */
                items.map((item, idx) => {
                  const isSelected = selectedIds.includes(item.id);
                  return (
                    <TableRow key={item.id} sx={{
                      "&:hover": { backgroundColor: isSelected ? "#EFF6FF" : C.surfaceAlt },
                      "&:hover .row-actions": { opacity: 1 },
                      backgroundColor: isSelected ? "#F0F7FF" : "transparent",
                      borderBottom: `1px solid ${C.border}`, transition: "background 0.12s", cursor: "default",
                    }}>
                      <TableCell sx={{ ...tdSx, px: "8px", width: 44 }}>
                        <Checkbox size="small" checked={isSelected} onChange={() => handleSelectOne(item.id)}
                          sx={{ p: 0.5, color: C.text3, "&.Mui-checked": { color: C.primary } }} />
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                          {(page - 1) * 10 + idx + 1}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.75rem", color: C.text2,
                          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                          fontWeight: 500, whiteSpace: "nowrap" }}>
                          {item.serial_number || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.825rem", fontWeight: 600, color: C.text1, whiteSpace: "nowrap" }}>
                          {item.name}
                        </Typography>
                        {item.procurement_item?.procurement_request && (
                          <Typography sx={{ fontSize: "0.7rem", color: C.text3, mt: 0.25 }}>
                            {item.procurement_item.procurement_request.requester_name} ·{" "}
                            {item.procurement_item.procurement_request.department}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                          minWidth: 28, height: 22, px: 1, borderRadius: "6px",
                          backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: C.text1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                            {item.quantity ?? 1} {item.unit || "Unit"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tdSx}><StatusChip map={CONDITION_MAP} value={item.condition} /></TableCell>
                      <TableCell sx={tdSx}><StatusChip map={STATUS_MAP} value={item.status} /></TableCell>
                      <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>{fmtDate(item.created_at)}</Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        {item.room ? (
                          <Box>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: C.text1, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                              {item.room.name}
                            </Typography>
                            <Typography sx={{ fontSize: "0.7rem", color: C.text3, fontFamily: "monospace" }}>
                              {item.room.code}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ color: C.text3, fontSize: "0.78rem" }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Box className="row-actions" sx={{ display: "flex", gap: 0.5, opacity: { xs: 1, md: 0.4 }, transition: "opacity 0.15s" }}>
                          <ActionBtn title="Lihat Detail" icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                            hoverColor={C.primary} hoverBg={C.primaryBg}
                            onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${item.id}`)} />
                          <ActionBtn title="Edit" icon={<EditIcon sx={{ fontSize: 14 }} />}
                            hoverColor={C.warn} hoverBg={C.warnBg}
                            onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${item.id}/edit`)} />
                          <ActionBtn title="Pindah Ruangan" icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                            hoverColor={C.success} hoverBg={C.successBg}
                            onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${item.id}/move`)} />
                          <ActionBtn title="Arsipkan" icon={<DeleteIcon sx={{ fontSize: 14 }} />}
                            hoverColor={C.danger} hoverBg={C.dangerBg}
                            onClick={() => { setDeleteId(item.id); setDeleteItem(item); }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            px: 2, py: "10px", borderTop: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
            <Typography sx={{ fontSize: "0.75rem", color: C.text3 }}>
              Halaman {page} dari {totalPages}
            </Typography>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
              size="small" color="primary"
              sx={{ "& .MuiPaginationItem-root": { fontSize: "0.75rem", borderRadius: "6px", minWidth: 28, height: 28 },
                "& .Mui-selected": { backgroundColor: `${C.primary} !important`, color: "#fff", fontWeight: 600 } }} />
          </Box>
        )}
      </Paper>

      {/* ── Single Arsip Dialog ── */}
      <Dialog open={Boolean(deleteId)} onClose={() => { setDeleteId(null); setDeleteItem(null); }}
        transitionDuration={{ enter: 120, exit: 90 }} disableRestoreFocus
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 12px 36px rgba(0,0,0,0.18)", willChange: "transform, opacity" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <DeleteIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>Arsipkan Barang?</Typography>
              {deleteItem && (
                <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                  <strong>{deleteItem.name}</strong> ({deleteItem.serial_number}) akan diarsipkan.
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px", backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text2, display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: C.success }} />
              Barang tidak akan dihapus permanen — bisa dipulihkan kapan saja.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => { setDeleteId(null); setDeleteItem(null); }}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>Batal</Button>
          <Button onClick={handleDelete} disabled={deleting} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.danger, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            {deleting ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Arsipkan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Arsip Confirm Dialog ── */}
      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)}
        transitionDuration={{ enter: 120, exit: 90 }} disableRestoreFocus
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 12px 36px rgba(0,0,0,0.18)", willChange: "transform, opacity" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ArchiveIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Arsipkan {selectedIds.length} Barang?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua barang yang dipilih akan diarsipkan sekaligus.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px", backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text2, display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: C.success }} />
              Barang tidak akan dihapus permanen — bisa dipulihkan kapan saja.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setBulkConfirmOpen(false)}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>Batal</Button>
          <Button onClick={handleBulkArchive} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.danger, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            Arsipkan Semua
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Import Excel Modal ── */}
      <Dialog open={importOpen} onClose={closeImport} maxWidth="sm" fullWidth
        transitionDuration={{ enter: 120, exit: 90 }} disableRestoreFocus
        PaperProps={{ sx: { borderRadius: "14px", boxShadow: "0 12px 36px rgba(0,0,0,0.18)", willChange: "transform, opacity" } }}>
        <Box sx={{ px: 3, pt: 3, pb: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "9px", backgroundColor: C.primaryBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FileUploadIcon sx={{ fontSize: 18, color: C.primary }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.text1, lineHeight: 1.2 }}>
                Import Barang via Excel
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>
                Format: .xlsx atau .xls, maks. 5MB
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={closeImport}
            sx={{ color: C.text3, borderRadius: "6px", "&:hover": { backgroundColor: C.surfaceAlt } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          {/* Download template banner */}
          <Box sx={{ p: "12px 14px", borderRadius: "10px", backgroundColor: C.primaryBg,
            border: `1px solid ${C.infoBdr}`, mb: 2.5,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: C.primary }}>
                Belum punya template?
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: C.text2, mt: 0.25 }}>
                Download template Excel yang sudah sesuai format sistem
              </Typography>
            </Box>
            <Button size="small" variant="outlined"
              startIcon={<FileDownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleDownloadTemplate}
              sx={{ borderColor: C.primary, color: C.primary, textTransform: "none",
                borderRadius: "8px", fontSize: "0.78rem", fontWeight: 600, whiteSpace: "nowrap",
                "&:hover": { backgroundColor: C.primary, color: "#fff" } }}>
              Download Template
            </Button>
          </Box>

          {/* Drop zone */}
          <Box onClick={() => !importing && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${importFile ? C.primary : C.border}`,
              borderRadius: "10px", p: 3, textAlign: "center",
              backgroundColor: importFile ? C.primaryBg : C.surfaceAlt,
              cursor: importing ? "not-allowed" : "pointer", transition: "all 0.2s",
              "&:hover": !importing ? { borderColor: C.primary, backgroundColor: C.primaryBg } : {},
            }}>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden
              onChange={(e) => { if (e.target.files[0]) { setImportFile(e.target.files[0]); setImportResult(null); }}} />
            {importFile ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                <InsertDriveFileIcon sx={{ fontSize: 28, color: C.primary }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: C.text1 }}>
                    {importFile.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>
                    {(importFile.size / 1024).toFixed(1)} KB · Klik untuk ganti file
                  </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                  sx={{ color: C.text3, "&:hover": { color: C.danger } }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : (
              <>
                <FileUploadIcon sx={{ fontSize: 32, color: C.text3, mb: 1 }} />
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: C.text2 }}>
                  Klik untuk pilih file
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: C.text3, mt: 0.5 }}>
                  Mendukung .xlsx, .xls, .csv
                </Typography>
              </>
            )}
          </Box>

          {/* Import result */}
          {importResult && (
            <Box sx={{ mt: 2, p: "12px 14px", borderRadius: "10px",
              backgroundColor: importResult.errors?.length > 0 ? C.warnBg : C.successBg,
              border: `1px solid ${importResult.errors?.length > 0 ? C.warnBdr : C.successBdr}` }}>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700,
                color: importResult.errors?.length > 0 ? C.warn : C.success, mb: 0.5 }}>
                {importResult.errors?.length > 0 ? "Import selesai dengan beberapa error" : "Import berhasil!"}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>
                {importResult.inserted} barang ditambahkan · {importResult.updated} diperbarui
                {importResult.errors?.length > 0 && ` · ${importResult.errors.length} baris error`}
              </Typography>
              {importResult.errors?.length > 0 && (
                <Box sx={{ mt: 1.5, maxHeight: 120, overflowY: "auto" }}>
                  {importResult.errors.map((e, i) => (
                    <Typography key={i} sx={{ fontSize: "0.72rem", color: C.danger, lineHeight: 1.6 }}>
                      Baris {e.row} ({e.serial}): {e.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={closeImport}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            {importResult ? "Tutup" : "Batal"}
          </Button>
          {!importResult && (
            <Button onClick={handleImport} disabled={!importFile || importing} variant="contained"
              startIcon={importing
                ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                : <FileUploadIcon sx={{ fontSize: "15px !important" }} />}
              sx={{ textTransform: "none", borderRadius: "8px", px: 2,
                backgroundColor: C.primary, fontWeight: 600, boxShadow: "none",
                "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "none" },
                "&:disabled": { backgroundColor: C.border } }}>
              {importing ? "Mengimpor..." : "Import Sekarang"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Modal Input Barang Sekaligus (Bulk) ── */}
      <BulkItemModal
        open={bulkOpen}
        onClose={handleCloseBulk}
        rooms={rooms}
        onSuccess={handleSuccessBulk}
      />

      {/* ── Floating Bulk Action Bar ── */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds([])}
        onBulkArchive={() => setBulkConfirmOpen(true)}
        onBulkExportPdf={handleBulkExportPdf}
        bulkArchiving={bulkArchiving}
        bulkExporting={bulkExporting}
      />
    </Box>
  );
}
